import React, { Component } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Dropdown, Form, Icon } from 'semantic-ui-react';
import BarcodeDisplay from './barcode-display';
import './price-sign-page.scss';

type PrintMode = 'normal' | 'sale' | 'club';
type PrintLayout = 'a4_big' | 'a4_strip3';

interface PriceSignPageState {
    barcodeValue: string;
    quantity: number;
    productName: string;
    productInfo: string;
    productSize: string;
    productUnit: string;
    imageSmUrl: string;
    price: number | null;
    salePrice: number | null;
    clubPrice: number | null;
    printMode: PrintMode;
    printLayout: PrintLayout;
}

interface LocationState {
    barcodeValue?: string;
    productName?: string;
    productInfo?: string | null;
    productSize?: string | number | null;
    productUnit?: string | null;
    price?: number | null;
    salePrice?: number | null;
    clubPrice?: number | null;
    imageSmUrl?: string | null;
}

class PriceSignPage extends Component<RouteComponentProps<{}, {}, LocationState>, PriceSignPageState> {
    private printPageStyleEl: HTMLStyleElement | null = null;

    constructor(props: RouteComponentProps<{}, {}, LocationState>) {
        super(props);

        const locationState = this.props.location.state;
        const price = this.parseOptionalNumber(locationState?.price);
        const salePrice = this.parseOptionalNumber(locationState?.salePrice);
        const clubPrice = this.parseOptionalNumber(locationState?.clubPrice);

        this.state = {
            barcodeValue: locationState?.barcodeValue || '',
            quantity: 1,
            productName: locationState?.productName || '',
            productInfo: locationState?.productInfo != null ? String(locationState.productInfo) : '',
            productSize: locationState?.productSize ? String(locationState.productSize) : '',
            productUnit: locationState?.productUnit || '',
            imageSmUrl: locationState?.imageSmUrl != null ? String(locationState.imageSmUrl) : '',
            price,
            salePrice,
            clubPrice,
            printMode: salePrice && salePrice > 0 ? 'sale' : (clubPrice && clubPrice > 0 ? 'club' : 'normal'),
            printLayout: 'a4_big'
        };
    }

    componentDidMount() {
        document.body.classList.add('price-sign-page');
    }

    componentWillUnmount() {
        document.body.classList.remove('price-sign-page');
        this.clearPrintClasses();
        this.teardownPrintPageStyle();
    }

    /**
     * iOS Safari does not honour `@page { size }` at all.
     * We detect it to apply a CSS-rotation workaround instead.
     */
    private isIos(): boolean {
        if (typeof navigator === 'undefined') return false;
        const ua = navigator.userAgent;
        if (/iPhone|iPod/i.test(ua)) return true;
        if (/iPad/i.test(ua)) return true;
        // iPadOS 13+ reports as Macintosh with touch points
        return /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
    }

    private clearPrintClasses = () => {
        document.documentElement.classList.remove('price-sign-print-active', 'price-sign-ios-print');
        document.body.classList.remove('price-sign-print-active', 'price-sign-ios-print');
    };

    private setupPrintPageStyle = () => {
        if (this.printPageStyleEl) return;
        const style = document.createElement('style');
        style.id = 'price-sign-page-print-override';
        // Explicit mm values have wider support than "A4 landscape" keyword on mobile browsers.
        // Appended last so this wins over any bundled @page rules (e.g. barcode-page.scss).
        style.textContent = '@media print { @page { size: 297mm 210mm; margin: 0; } }';
        document.head.appendChild(style);
        this.printPageStyleEl = style;
    };

    private teardownPrintPageStyle = () => {
        if (this.printPageStyleEl) {
            this.printPageStyleEl.remove();
            this.printPageStyleEl = null;
        }
    };

    /** Same resolution as `ImageOnS3`: S3 object key or absolute URL. */
    private resolveProductImageUrl(keyOrUrl: string): string {
        const raw = (keyOrUrl || '').trim();
        if (!raw) {
            return '';
        }
        if (/^https?:\/\//i.test(raw)) {
            return raw;
        }
        const bucket = process.env.REACT_APP_S3_BUCKET;
        const region = process.env.REACT_APP_S3_REGION;
        if (!bucket || !region) {
            return raw;
        }
        return `https://${bucket}.s3.${region}.amazonaws.com/${raw}`;
    }

    parseOptionalNumber(value: any): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
    }

    hasSalePrice = () => {
        return !!(this.state.salePrice && this.state.salePrice > 0);
    };

    hasClubPrice = () => {
        return !!(this.state.clubPrice && this.state.clubPrice > 0);
    };

    getCurrentPrice = () => {
        const { printMode, price, salePrice } = this.state;
        if (printMode === 'sale' && salePrice) {
            return salePrice;
        }
        if (printMode === 'club') {
            // Show sale price (or regular price) — never the club price value
            return (salePrice && salePrice > 0) ? salePrice : (price || 0);
        }
        return price || 0;
    };

    getCurrentLabel = () => {
        if (this.state.printMode === 'sale') {
            return 'ХЯМДАРСАН ҮНЭ';
        }
        if (this.state.printMode === 'club') {
            return 'КЛУБ ҮНЭТЭЙ';
        }
        return 'ҮНЭ';
    };

    formatPrice = (value: number) => {
        return `${value.toLocaleString()} ₮`;
    };

    shouldShowSecondaryClubPrice = () => {
        return this.state.printMode === 'sale' && this.hasClubPrice();
    };

    shouldShowStrikePrice = () => {
        const basePrice = this.state.price || 0;
        const effectivePrice = this.getCurrentPrice();
        return basePrice > 0 && effectivePrice > 0 && effectivePrice !== basePrice;
    };

    parseSizeValue = (value: string): number | null => {
        if (!value) {
            return null;
        }
        const normalized = String(value).trim().replace(',', '.');
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : null;
    };

    getPerUnitInfo = (price: number) => {
        const size = this.parseSizeValue(this.state.productSize);
        if (!size || size <= 0) {
            return null;
        }

        const unit = (this.state.productUnit || '').trim().toLowerCase();
        const isKgUnit = ['kg', 'kgs', 'kilogram', 'kilograms', 'кг', 'килограмм'].includes(unit);
        const isGramUnit = ['g', 'gr', 'gram', 'grams', 'гр', 'грамм'].includes(unit);
        const isLiterUnit = ['l', 'lt', 'ltr', 'liter', 'litre', 'литр', 'л'].includes(unit);
        const isMlUnit = ['ml', 'milliliter', 'millilitre', 'миллилитр', 'мл'].includes(unit);

        if (isKgUnit) {
            if (size < 1) {
                return null;
            }
            return { label: 'ҮНЭ / КГ', value: price / size };
        }

        if (isGramUnit) {
            if (size < 1000) {
                return null;
            }
            return { label: 'ҮНЭ / КГ', value: price / (size / 1000) };
        }

        if (isLiterUnit) {
            if (size < 1) {
                return null;
            }
            return { label: 'ҮНЭ / ЛИТР', value: price / size };
        }

        if (isMlUnit) {
            if (size < 1000) {
                return null;
            }
            return { label: 'ҮНЭ / ЛИТР', value: price / (size / 1000) };
        }

        return null;
    };

    onPrintModeChange = (_event: any, data: any) => {
        this.setState({ printMode: data.value as PrintMode });
    };

    printNow = () => {
        const ios = this.isIos();

        // Add class to html so we can scope `html { overflow: hidden }` without touching other print flows.
        document.documentElement.classList.add('price-sign-print-active');
        document.body.classList.add('price-sign-print-active');

        if (ios) {
            // iOS ignores @page { size }; rotate content via CSS instead (no @page injection needed).
            document.documentElement.classList.add('price-sign-ios-print');
            document.body.classList.add('price-sign-ios-print');
        } else {
            // Desktop + Android: explicit mm @page overrides bundled landscape/portrait rules.
            this.setupPrintPageStyle();
        }

        const clearPrintScope = () => {
            this.clearPrintClasses();
            this.teardownPrintPageStyle();
            window.removeEventListener('afterprint', clearPrintScope);
        };

        window.addEventListener('afterprint', clearPrintScope);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                window.print();
            });
        });
        // Fallback cleanup in case afterprint never fires (some mobile browsers omit it).
        window.setTimeout(clearPrintScope, 5000);
    };

    goBack = () => {
        const historyAny: any = this.props.history;
        if (typeof historyAny.goBack === 'function') {
            historyAny.goBack();
            return;
        }
        if (typeof historyAny.go === 'function') {
            historyAny.go(-1);
            return;
        }
        window.history.back();
    };

    renderPrintModeOptions = () => {
        const options: any[] = [{ key: 'normal', value: 'normal', text: 'Үнэ' }];
        if (this.hasSalePrice()) {
            options.push({ key: 'sale', value: 'sale', text: 'Хямдарсан үнэ' });
        }
        if (this.hasClubPrice()) {
            options.push({ key: 'club', value: 'club', text: 'Клуб үнэтэй' });
        }
        return options;
    };

    renderPrintLayoutOptions = () => {
        return [
            { key: 'a4_big', value: 'a4_big', text: 'A4 томоор' },
            { key: 'a4_strip3', value: 'a4_strip3', text: 'A4 1/3' }
        ];
    };

    onPrintLayoutChange = (_event: any, data: any) => {
        const nextLayout = data.value as PrintLayout;
        const nextQuantity = nextLayout === 'a4_strip3' ? 1 : 1;
        this.setState({ printLayout: nextLayout, quantity: nextQuantity });
    };

    renderSigns = () => {
        const signs = [];
        const { quantity, barcodeValue, productName, productInfo, productSize, productUnit, printMode, printLayout } = this.state;
        const effectiveQuantity = printLayout === 'a4_strip3' ? 1 : quantity;
        const signPrice = this.getCurrentPrice();
        const signLabel = this.getCurrentLabel();
        const itemCode = barcodeValue || '-';
        const sizeUnitLabel = `${productSize || ''} ${productUnit || ''}`.trim();
        const perUnitInfo = this.getPerUnitInfo(signPrice);
        const productImageSrc = this.resolveProductImageUrl(this.state.imageSmUrl);

        for (let i = 0; i < effectiveQuantity; i++) {
            signs.push(
                <div
                    key={i}
                    className={'price-sign-item' + (productImageSrc ? ' price-sign-item--with-image' : '')}
                >
                    <div className={'price-sign-content' + (productImageSrc ? ' price-sign-content--with-image' : '')}>
                        <div className="price-sign-top">
                            <div className="barcode-corner">
                                <BarcodeDisplay
                                    value={barcodeValue}
                                    width={2}
                                    height={28}
                                    fontSize={10}
                                    margin={0}
                                />
                            </div>
                            {/* <div className="item-code-label">{itemCode}</div> */}
                            <div className="product-name-label">{(productName || '-').toUpperCase()}</div>
                            <div className="product-size-label">{sizeUnitLabel || '-'}</div>
                            {productInfo.trim() ? (
                                <div className="product-description-label">{productInfo.trim()}</div>
                            ) : null}
                            {perUnitInfo ? (
                                <div className="per-unit-price-label">
                                    <span>{perUnitInfo.label}</span>
                                    <strong>{this.formatPrice(Math.round(perUnitInfo.value))}</strong>
                                </div>
                            ) : null}
                        </div>
                        {productImageSrc ? (
                            <div className="price-sign-product-image-slot">
                                <img src={productImageSrc} alt="" />
                            </div>
                        ) : null}
                        <div className="price-sign-footer">
                            <div className={`product-price-label ${printMode === 'sale' ? 'discount-highlight' : ''}`}>
                                {(printMode === 'club' || this.shouldShowStrikePrice()) ? (
                                    <div className="price-original-line">
                                        <span className={`price-badge ${printMode === 'club' ? 'club-badge' : 'sale-badge'}`}>
                                            {printMode === 'club' ? 'КЛУБ ҮНЭТЭЙ' : 'ХЯМДАРСАН ҮНЭ'}
                                        </span>
                                    </div>
                                ) : null}
                                <div className="price-effective-line">
                                    {this.shouldShowStrikePrice() ? (
                                        <span className="price striked">{this.formatPrice(this.state.price || 0)}</span>
                                    ) : null}
                                    <div className="price-value">{this.formatPrice(signPrice)}</div>
                                </div>
                                {this.shouldShowSecondaryClubPrice() ? (
                                    <div className="secondary-price-line">
                                        <span className="price-tag club">КЛУБ ҮНЭТЭЙ</span>
                                        <span className="secondary-price-value">{this.formatPrice(this.state.clubPrice || 0)}</span>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return signs;
    };

    render() {
        return (
            <div className={`price-sign-page-container layout-${this.state.printLayout}`}>
                <div className="price-sign-controls no-print">
                    <a onClick={this.goBack}>
                        <Icon name="angle left" />
                        <span className="underline">Буцах</span>
                    </a>
                    <Form>
                        <Form.Group widths="equal">
                            <Form.Input label="Бүтээгдэхүүний нэр" value={this.state.productName || ''} readOnly />
                            <Form.Input
                                type="number"
                                min={1}
                                label="Хэвлэх тоо"
                                value={this.state.quantity}
                                onChange={(e: any) => this.setState({ quantity: Number(e.target.value) || 1 })}
                            />
                            <Form.Field>
                                <label>Хэвлэх төрөл</label>
                                <Dropdown
                                    fluid
                                    selection
                                    options={this.renderPrintModeOptions()}
                                    value={this.state.printMode}
                                    onChange={this.onPrintModeChange}
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>Хэвлэх загвар</label>
                                <Dropdown
                                    fluid
                                    selection
                                    options={this.renderPrintLayoutOptions()}
                                    value={this.state.printLayout}
                                    onChange={this.onPrintLayoutChange}
                                />
                            </Form.Field>
                        </Form.Group>
                        <Button primary onClick={this.printNow}>Хэвлэх</Button>
                    </Form>
                </div>
                <div className="price-sign-grid-container">
                    {/* <div className="print-title">
                        <div className="brand-main">Title</div>
                        <div className="brand-sub">ITEM SIGNS</div>
                    </div> */}
                    <div className="price-sign-grid">
                        {this.renderSigns()}
                    </div>
                </div>
            </div>
        );
    }
}

export default PriceSignPage;
